/**
 * App-wide toast notifications. Up to three stay on screen; extras wait in a
 * FIFO queue and pop in when a visible slot clears. Desktop anchors to the
 * bottom of the page, mobile to the top (below the top bar).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ToastViewport } from "@/components/toast/ToastViewport";
import {
  type ToastDismissReason,
  type ToastInput,
  type ToastRecord,
  type ToastState,
  dismissToast,
  emptyToastState,
  enqueueToast,
  toToastRecord,
} from "@/lib/toastStore";

export type { ToastDismissReason, ToastInput } from "@/lib/toastStore";

interface ToastTimer {
  timeoutId: number;
  remainingMs: number;
  startedAt: number;
}

interface ToastApi {
  toast: (input: ToastInput) => string;
  dismiss: (id: string, reason?: ToastDismissReason) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
}

interface ToastContextValue extends ToastApi {
  toasts: ToastRecord[];
}

const ToastApiContext = createContext<ToastApi | null>(null);
const ToastListContext = createContext<ToastRecord[]>([]);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ToastState>(emptyToastState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const timersRef = useRef<Map<string, ToastTimer>>(new Map());
  const dismissRef = useRef<(id: string, reason?: ToastDismissReason) => void>(
    () => {},
  );

  const clearTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (!timer) return;
    if (timer.timeoutId) window.clearTimeout(timer.timeoutId);
    timersRef.current.delete(id);
  }, []);

  const startTimer = useCallback(
    (toast: ToastRecord, remainingMs = toast.durationMs) => {
      if (toast.durationMs <= 0 || remainingMs <= 0) return;
      const existing = timersRef.current.get(toast.id);
      if (existing?.timeoutId) window.clearTimeout(existing.timeoutId);
      const timeoutId = window.setTimeout(() => {
        dismissRef.current(toast.id, "timeout");
      }, remainingMs);
      timersRef.current.set(toast.id, {
        timeoutId,
        remainingMs,
        startedAt: Date.now(),
      });
    },
    [],
  );

  const dismiss = useCallback(
    (id: string, reason: ToastDismissReason = "dismiss") => {
      const { state: next, dismissed, promoted } = dismissToast(
        stateRef.current,
        id,
      );
      if (!dismissed) return;
      clearTimer(id);
      if (reason === "action") {
        dismissed.action?.onAction();
      } else {
        void dismissed.onCommit?.();
      }
      stateRef.current = next;
      setState(next);
      if (promoted) startTimer(promoted);
    },
    [clearTimer, startTimer],
  );
  dismissRef.current = dismiss;

  const toast = useCallback(
    (input: ToastInput) => {
      const record = toToastRecord(input);
      const next = enqueueToast(stateRef.current, record);
      stateRef.current = next;
      setState(next);
      if (next.visible.some((item) => item.id === record.id)) {
        startTimer(record);
      }
      return record.id;
    },
    [startTimer],
  );

  const pause = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (!timer?.timeoutId) return;
    window.clearTimeout(timer.timeoutId);
    const elapsed = Date.now() - timer.startedAt;
    timersRef.current.set(id, {
      timeoutId: 0,
      remainingMs: Math.max(0, timer.remainingMs - elapsed),
      startedAt: timer.startedAt,
    });
  }, []);

  const resume = useCallback(
    (id: string) => {
      const timer = timersRef.current.get(id);
      const record = stateRef.current.visible.find((item) => item.id === id);
      if (!timer || !record || timer.timeoutId) return;
      startTimer(record, timer.remainingMs);
    },
    [startTimer],
  );

  useEffect(() => {
    return () => {
      for (const timer of timersRef.current.values()) {
        if (timer.timeoutId) window.clearTimeout(timer.timeoutId);
      }
      timersRef.current.clear();
    };
  }, []);

  const api = useMemo(
    () => ({ toast, dismiss, pause, resume }),
    [dismiss, pause, resume, toast],
  );

  return (
    <ToastApiContext.Provider value={api}>
      <ToastListContext.Provider value={state.visible}>
        {children}
        <ToastViewport
          toasts={state.visible}
          dismiss={dismiss}
          pause={pause}
          resume={resume}
        />
      </ToastListContext.Provider>
    </ToastApiContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const api = useContext(ToastApiContext);
  const toasts = useContext(ToastListContext);
  if (!api) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return { ...api, toasts };
}

/** Stable imperative API — does not change when the visible stack updates. */
export function useToastOptional(): ToastApi | null {
  return useContext(ToastApiContext);
}
